/// <amd-module name="WidgetCommon/ModernWidgetTypes/ViewComponentBase" />
import * as WidgetTelemetry from "Dashboards/WidgetCommon/WidgetTelemetry";
import { LoadedEventInfo, LoadingStatus, WidgetProps, WidgetSize } from "Dashboards/WidgetContracts/WidgetProps";
import * as React from "react";
import {
    ComponentLayoutEngine,
    ComponentLayoutMapping,
    DefaultComponentLayouts
} from "Reporting/Visuals/LayoutComponents/Widgets/ComponentLayoutEngine";
import { LayoutActions } from "Reporting/Visuals/LayoutComponents/Widgets/LayoutActions";
import { ILayoutComponentFactory } from "Reporting/Visuals/LayoutComponents/Widgets/LayoutComponents";
import { LayoutState, MessageType, packMessageAsState } from "Reporting/Visuals/LayoutComponents/Widgets/LayoutState";
import { LayoutStore } from "Reporting/Visuals/LayoutComponents/Widgets/LayoutStore";
import { VssComponent } from "VSS/Platform/Layout";
import { ActionCreatorBase } from "./ActionCreatorBase";
import { ISettingsManager } from "./SettingsManagerBase";
import { WidgetDataManagerBase, WidgetDataManagerOptions } from "./WidgetDataManagerBase";
import { MessageOptions, WidgetMessageCardFactory } from "./WidgetMessageCardFactory";

export interface ViewComponentProps extends WidgetProps {
    /*** Contract for signalling to consumer of render completion. Consumer must instantiate initial deferred object, in order to receive subsequent notification. */
    onRenderCompletion: (error?: LoadedEventInfo) => void;
    suppressAnimations: boolean;
}

/** Type-placeholder for Widget's locally defined Custom Settings type */
export interface WidgetCustomSettings {}

/** Encapsulates common state and flow patterns on View Component layer */
export abstract class ViewComponentBase<P extends ViewComponentProps, WidgetCustomSettings> extends VssComponent<P, LayoutState> {
    /** Describes if first-time async-settings initialization flow has had a chance to run or fail, for properly bootstrapping the data manager which manages the state object. */
    private settingsAreInitialized: boolean = false;
    protected store: LayoutStore;
    protected actions: LayoutActions;
    protected actionsCreator: ActionCreatorBase;
    protected layoutFactory: ILayoutComponentFactory<LayoutState>;

    protected dataManager: WidgetDataManagerBase;
    protected settingsManager: ISettingsManager<WidgetCustomSettings>;

    public componentDidUpdate(prevProps: P, prevState: LayoutState) {
        super.componentDidUpdate(prevProps, prevState);

        if (!this.sizesAreSame(prevProps.size, this.props.size)) {
            this.layoutFactory = this.selectLayoutFactory();
            // Register demands, and request initial demanded state be provided via actionCreator (Note: this is synchronous, with no actual requests going out yet).
            this.layoutFactory.registerDemands(this.actionsCreator);
            // re-run settings flux flow with revised layout demands, to ensure any data gaps from demands are executed, and state is propagated.
            this.ensureSettings();
        } else if (this.props.settings !== prevProps.settings) {
            this.ensureSettings();
        }
    }

    // Not marking as async so any synchronous errors get passed up to widget host
    public componentWillMount(): void {
        this.initializeCommonMembers();
        this.ensureSettings();
    }

    /** Render Happy-Path/Message State experience based on state */
    public render(): JSX.Element | null {
        if (!this.settingsAreInitialized) {
            return null;
        }

        try {
            return this.getStoreState().showMessage ? this.renderMessageCard() : this.renderView();
        } catch (error) {
            this.handleErrorOnRenderCompletion(error);
            return null;
        }
    }

    public componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.handleErrorOnRenderCompletion(error);
    }

    public componentDidMount(): void {
        this.store && this.store.addChangedListener(this.setStoreStateDelegate);
        // Log telemetry with user specified configuration settings to help understand data about most popular settings
        if (this.props.settings) {
            WidgetTelemetry.onWidgetLoaded(
                this.context.pageContext,
                this.props,
                this.packWidgetLoadedTelemetryData(JSON.parse(this.props.settings!))
            );
        }
    }

    public componentWillUnmount(): void {
        this.store && this.store.removeChangedListener(this.setStoreStateDelegate);
    }

    protected setStoreStateAndRenderCompletion() {
        this.setStoreState();
        this.props.onRenderCompletion({
            outcome: LoadingStatus.success
        });
    }
    protected setStoreStateDelegate = () => this.setStoreState();

    /**
     * handles boilerplate one-time creation of all flux-oriented members on the ViewComponent.
     */
    protected initializeCommonMembers() {
        // Perform one time-setup of supporting state & action facilities.
        this.dataManager = this.instantiateDataManager();
        this.actions = new LayoutActions();
        this.store = new LayoutStore(this.actions, this.dataManager.getInitialState());
        this.actionsCreator = this.createActionCreator();

        this.layoutFactory = this.selectLayoutFactory();
        // Register demands, and request initial demanded state be provided via actionCreator (Note: this is synchronous, with no actual requests going out yet).
        this.layoutFactory.registerDemands(this.actionsCreator);
        this.actionsCreator.initialize();
        this.setState(this.getStoreState());
        this.settingsManager = this.createSettingsManager();
    }

    protected instantiateDataManager(): WidgetDataManagerBase {
        const changePayload = { ...this.props };
        const dataManagerOptions = {
            title: changePayload.name,
            hideTitle: changePayload.shouldNotShowTitle,
            settings: changePayload.settings,
            suppressAnimations: this.props.suppressAnimations,
            widgetTelemetryProps: this.props
        };
        return this.createDataManager(dataManagerOptions);
    }

    /** Handles updating the UI based on he current Widget Configuration. */
    protected updateWithLatestConfiguration(dataManagerOptions: WidgetDataManagerOptions, customSettings: WidgetCustomSettings) {
        /*
         * These two steps are tightly coupled. Once we have the configuration, we can
         * request the data to be loaded asynchronously.
         */
        this.dataManager.update(dataManagerOptions);
        this.actionsCreator.requestData().then(
            success => {
                this.props.onRenderCompletion();
            },
            error => {
                // Pass whatever error was captured, up for container to interpret.
                this.handleErrorOnRenderCompletion(error);
            }
        );
    }

    /** Extension point for widgets to add their unique telemetry  */
    protected packWidgetLoadedTelemetryData(customSettings: WidgetCustomSettings): { [key: string]: any } {
        return {};
    }

    /** Required by derived types: Creates concrete data manager associated with the widget. */
    protected abstract createDataManager(options: WidgetDataManagerOptions): WidgetDataManagerBase;

    /** Required by derived types: Creates concrete action creator associated with the widget. */
    protected abstract createActionCreator(): ActionCreatorBase;

    /** Responsible for providing a settings manager implementation, which handles preparation of default settings. */
    protected abstract createSettingsManager(): ISettingsManager<WidgetCustomSettings>;

    /** Describes the set of layout mappings supported by this widget. */
    protected getLayoutMappings(): ComponentLayoutMapping[] {
        return DefaultComponentLayouts.getDefaultMappings();
    }

    /** Instantiates a layout factory, with assumption of default layouts. */
    protected selectLayoutFactory(): ILayoutComponentFactory<LayoutState> {
        const layoutEngine = new ComponentLayoutEngine();
        const layoutFactory = layoutEngine.selectLayoutFactory(this.props.size, this.getLayoutMappings());
        return layoutFactory;
    }

    protected handleErrorOnRenderCompletion(error: any): void {
        this.props.onRenderCompletion({
            outcome: LoadingStatus.failure,
            outcomeDescription: error && error.message ? error.message : error
        });
    }

    /**
     * Returns additional options for particular message types that should be given to the WidgetMessageCardFactory.
     */
    protected abstract getMessageOptions(): MessageOptions | undefined;

    /** Render message card for atypical cases */
    protected renderMessageCard(): JSX.Element | null {
        const title = this.props.name;
        const size = this.props.size;
        const message = this.getStoreState().message;
        const messageType = this.getStoreState().messageType;
        const messageOptions = this.getMessageOptions();

        return (
            <WidgetMessageCardFactory
                messageType={messageType}
                message={message}
                messageOptions={messageOptions}
                title={title}
                size={size}
                hideTitle={this.props.shouldNotShowTitle}
            />
        );
    }

    protected setStoreState(): void {
        this.setState(this.getStoreState());
    }

    protected getStoreState(): LayoutState {
        return {
            ...this.store.getState(),
            title: {
                text: this.props.name, // We dont persist title in the store state. We should always reflect the props Title that is passed in
                hideTitle: this.props.shouldNotShowTitle
            }
        };
    }

    protected handleSettingsError(error: Error) {
        // While widget settings are not in a valid state, we must fall back on the unconfigured experience.
        this.store.setState(packMessageAsState(MessageType.Unconfigured));
        this.setStoreStateAndRenderCompletion();
    }

    private sizesAreSame(size1: WidgetSize, size2: WidgetSize): boolean {
        return size1.height === size2.height && size1.width === size2.width;
    }

    private async ensureSettings(): Promise<void> {
        /*
         * At this point, we should be aware of the demands we need to satisfy. However, we are not guaranteed
         * to have the configuration state for widget rendering
         */

        try {
            const settings: WidgetCustomSettings = await this.settingsManager.ensureInitialSettings(this.props);
            const changeWithDefaults: ViewComponentProps = { ...this.props };

            changeWithDefaults.settings = JSON.stringify(settings);

            const dataManagerOptions = {
                title: changeWithDefaults.name,
                hideTitle: changeWithDefaults.shouldNotShowTitle,
                settings: changeWithDefaults.settings,
                suppressAnimations: this.props.suppressAnimations,
                widgetTelemetryProps: { ...this.props, settings: changeWithDefaults.settings }
            };

            // Log telemetry with settings to help understand data about how many users use default vs customized settings
            WidgetTelemetry.onWidgetLoading(this.context.pageContext, this.props, this.packWidgetLoadedTelemetryData(settings));
            this.settingsAreInitialized = true;

            this.updateWithLatestConfiguration(dataManagerOptions, settings);
        } catch (error) {
            this.settingsAreInitialized = true;
            this.handleSettingsError(error);
        }
    }

    /** Render the happy path view */
    private renderView(): JSX.Element | null {
        return <div className="view-component-base">{this.layoutFactory.render(this.getStoreState())}</div>;
    }
}
